import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import Barcode from "react-barcode";

import { SERVICE_DETAIL_ITEM_TYPE } from "../../../consts/serviceTemplate";

import { MemberShipCardInfo } from "../../../types/memberShipCardTypes";

import { getMemberShipInfo } from "../../../services/memberShipCardServices";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonPopup from "../../../components/Popup";
import CommonButton from "../../../components/Button";
import HorizontalRule from "../../../components/Rule/Horizontal";
import BottomGroup from "../../../components/Button/Group";

type Props = {};

export default function MemberShipCard(props: Props) {
  const [info, setInfo] = useState<MemberShipCardInfo>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [popup, setPopup] = useState<string>("");
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const loadingComponent = useFullScreenLoading({ isLoading });

  const loadMemberShipInfo = async () => {
    setIsLoading(true);
    try {
      const res = await getMemberShipInfo();
      if (Number(res.data.resultCode) > 1) {
        open(res.data.message);
      } else {
        if (res.data.serviceDetailType === SERVICE_DETAIL_ITEM_TYPE.OUR_HOME) {
          setInfo(res.data);
        } else {
          open("멤버십 정보 로드 실패", {
            onClick: () => {
              navigate("/view");
            },
            enableDimmedClose: false,
          });
        }
      }
    } catch (e: any) {
      if (e?.response?.data?.errorCode) {
        open(e?.response?.data?.errorCode);
      } else {
        open("멤버십 정보 로드 실패");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (info?.cardNo === null) {
      setPopup("needSignUp");
    }
  }, [info?.cardNo]);

  useEffect(() => {
    loadMemberShipInfo();
  }, []);

  if (isLoading && !info) return <div></div>;

  return (
    <div className="flex flex-col gap-5 pb-10">
      {loadingComponent}
      <StickyHeader
        title="사원카드 잔액조회"
        onBackClick={() => navigate("/view")}
        className="px-5"
        rightContent={
          <div
            onClick={() => {
              loadMemberShipInfo();
            }}
          >
            <MemberShipCardRefreshIcon />
          </div>
        }
      />
      {isLoading || info?.cardNo === null ? (
        <div></div>
      ) : (
        <>
          <div className="flex flex-col">
            <div className="flex justify-between px-5 leading-[19px]">
              <div className=" font-medium">잔액</div>
              <div className=" text-2xl font-semibold">
                {(Number(info?.amount) ?? 0).toLocaleString()}원
              </div>
            </div>

            <div className="py-10 px-5 flex justify-center">
              <div className="h-[180px] w-[180px] flex items-center justify-center">
                <QRCode value={info?.cardNo ?? ""} />
              </div>
            </div>

            <div className="px-5">
              <HorizontalRule margin="0" className="bg-[#E7E7E8]" />
            </div>

            <div className="py-[30px] flex flex-col gap-2.5 px-5">
              <div className="h-bottom-button w-full flex items-center justify-center">
                <Barcode
                  value={info?.cardNo ?? ""}
                  height={50}
                  displayValue={false}
                />
              </div>
              <div className="flex justify-center text-sm leading-5 tracking-[-0.14px]">
                {info?.cardNo}
              </div>
            </div>

            <div className="px-5">
              <div className="py-5 px-2.5 rounded-[3px] whitespace-pre-line text-center bg-brand-weather-badge-background text-brand-neutral-80">
                {
                  "아워홈에서 운영하는 식당, 커피전문점 등의 매장에서\n간편하게 결제하실 수 있습니다.\n"
                }
                <span className="text-brand-primary-magenta font-semibold">
                  {
                    "※ 상주 중인 건물에서만 결제 가능하며\n건물 상황에 따라 적용 매장이 상이할 수 있습니다."
                  }
                </span>
                {
                  "\n\n- 적용매장 -\n건강한 식탁(구내식당),cafegran(커피전문점),\nBESTJOY(매점), 인더박스(간편식)"
                }
              </div>
            </div>
          </div>

          <BottomGroup
            className="grid grid-cols-2 gap-1.5 w-full pb-5 p-5 !h-[90px]"
            zIndex="middle"
          >
            <CommonButton
              variant="tertiary"
              className=" text-brand-neutral-60 w-full h-full"
              onClick={() => navigate("usage")}
            >
              이용내역
            </CommonButton>
            <a href={info?.url ?? ""} target="_blank" className="w-full flex-1">
              <CommonButton
                className="p-4 text-brand-neutral-60 w-full h-full"
                variant="tertiary"
              >
                아워홈 Mall
              </CommonButton>
            </a>
          </BottomGroup>
        </>
      )}

      <CommonPopup unUseHorizonRule isOpen={popup === "needSignUp"} padding={0}>
        <div className=" whitespace-pre-line px-5 pb-[40px] pt-[50px] text-center text-lg font-medium leading-6">
          {"아워홈 회원가입이 필요합니다.\n회원가입을 진행하시겠습니까?"}
        </div>

        <div className="w-full flex">
          <CommonButton
            variant="secondary"
            className="rounded-none w-[100px] h-bottom-button"
            onClick={() => {
              setPopup("");
              navigate("/view");
            }}
          >
            아니오
          </CommonButton>
          <CommonButton
            className="rounded-none h-bottom-button w-full"
            onClick={() => {
              window.open(info?.url ?? "");
            }}
          >
            예
          </CommonButton>
        </div>
      </CommonPopup>

      {simplePopupComponent}
    </div>
  );
}

export const MemberShipCardRefreshIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.85331 8.77173L4.50015 11.6241C4.52542 11.6679 4.58137 11.6829 4.62513 11.6576L7.47754 10.0108"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M4.68213 11.6742C4.79267 8.94731 6.25554 6.33445 8.79384 4.86895C12.7322 2.59514 17.7682 3.94452 20.042 7.88288C22.3158 11.8212 20.9664 16.8572 17.028 19.131C14.3432 20.6811 11.1482 20.5474 8.66604 19.0574C7.50715 18.3618 6.50363 17.3706 5.77992 16.1171"
      stroke="black"
      strokeWidth="1.75"
    />
  </svg>
);
